import { c as _c } from "react/compiler-runtime";
import { useAnalytics } from '@repo/analytics/analytics-provider';
import { Button } from '@repo/design-system/components/ds/button';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Type } from '@repo/design-system/components/ui/type';
import { useHostType } from '@repo/host/hooks/use-host-info';
import { useT } from '@repo/i18n/components/i18n.client';
import { css } from '@repo/panda/css';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExploreAnchorPinId, setActiveCustomChecklistId, setSidebarView } from '@repo/store/maps/slices/clientContext';
import { selectExternalMapId, selectFilterState, selectMapData, selectSteamId } from '@repo/store/maps/slices/mapData';
import type { WebLoginPromptTrigger } from '@repo/types/analytic-events/website-analytic-events';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import { getPinImageUrl, getSteamImageUrl } from '@repo/utility/image-urls';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { buildSubcategoryMap } from '../checklist/checklist-utils';
import { PinListSidebarShell } from '../checklist/pin-list-sidebar-shell';
import { SaveChecklistModal } from '../checklist/save-checklist-modal';
import { useMapPinOperations } from '../game-map/hooks/useMapPinOperations';
import { ExploreActionHeaderIcon } from './explore-action-header-icon';
import { HiddenPinsCard } from './hidden-pins-card';
import { useExploreNearbyPins } from './use-explore-nearby-pins';
interface ExploreSidebarViewProps {
  onBack: () => void;
  isMobilePane?: boolean;
  onSearchFocus?: () => void;
}
export const categoryIconClass = css({
  width: '[48px]',
  height: '[48px]',
  minWidth: '[48px]',
  minHeight: '[48px]',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '2xl',
  color: 'white',
  boxShadow: 'sm',
  backdropFilter: '[blur(8px)]',
  border: 'none',
  cursor: 'pointer',
  transition: '[transform 0.1s ease]',
  _hover: {
    transform: 'scale(1.05)'
  },
  _focusVisible: {
    outline: '[2px solid]',
    outlineColor: 'blue.500',
    outlineOffset: '2'
  }
});
const HERO_IMAGE_HEIGHT_PX = 232;
export const HERO_IMAGE_SIZE_OVERRIDE: React.CSSProperties = {
  backgroundSize: 'cover',
  backgroundPosition: 'top center',
  backgroundRepeat: 'no-repeat',
  height: `${HERO_IMAGE_HEIGHT_PX}px`
};
export function getFeatureImageUrl(feature: MapDataLocationFeature | undefined): string | null {
  if (!feature) return null;
  return getPinImageUrl(feature.properties.imageId, feature.properties.imagePath, 750);
}
export const ExploreSidebarView: React.FC<ExploreSidebarViewProps> = t0 => {
  const $ = _c(95);
  const {
    onBack,
    isMobilePane: t1,
    onSearchFocus
  } = t0;
  const isMobilePane = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const mapData = useAppSelector(selectMapData);
  const externalMapId = useAppSelector(selectExternalMapId);
  const steamId = useAppSelector(selectSteamId);
  const anchorPinId = useAppSelector(selectExploreAnchorPinId);
  const isLoggedIn = useAppSelector(_temp);
  const {
    selectMarkerInMap
  } = useMapPinOperations();
  const dispatch = useAppDispatch();
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [loginModalTrigger, setLoginModalTrigger] = useState("checklist-item");
  const [showHiddenPins, setShowHiddenPins] = useState(false);
  const [hiddenCardDismissed, setHiddenCardDismissed] = useState(false);
  const filterState = useAppSelector(selectFilterState);
  const analytics = useAnalytics();
  const hostType = useHostType();
  let t3;
  bb0: {
    if (!anchorPinId || !mapData) {
      t3 = null;
      break bb0;
    }
    let t4;
    if ($[1] !== anchorPinId || $[2] !== mapData.geojson.features) {
      t4 = mapData.geojson.features.find(feature => feature.geometry.type === "Point" && feature.properties.externalId === anchorPinId) ?? null;
      $[1] = anchorPinId;
      $[2] = mapData.geojson.features;
      $[3] = t4;
    } else {
      t4 = $[3];
    }
    t3 = t4;
  }
  const anchorPin = t3;
  let t4;
  let t5;
  if ($[4] !== anchorPin || $[5] !== mapData || $[6] !== onBack) {
    t4 = () => {
      if (mapData && !anchorPin) {
        onBack();
      }
    };
    t5 = [mapData, anchorPin, onBack];
    $[4] = anchorPin;
    $[5] = mapData;
    $[6] = onBack;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const nearbyPins = useExploreNearbyPins(anchorPin, mapData);
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setShowHiddenPins(false);
      setHiddenCardDismissed(false);
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== anchorPinId) {
    t7 = [anchorPinId];
    $[10] = anchorPinId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  if ($[12] !== anchorPin || $[13] !== nearbyPins) {
    t8 = anchorPin ? [anchorPin, ...nearbyPins] : nearbyPins;
    $[12] = anchorPin;
    $[13] = nearbyPins;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const allFeatures = t8;
  let t9;
  if ($[15] !== filterState.hiddenSubcategories) {
    t9 = new Set(filterState.hiddenSubcategories);
    $[15] = filterState.hiddenSubcategories;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const hiddenSubcategorySet = t9;
  let t10;
  if ($[17] !== hiddenSubcategorySet) {
    t10 = (n, f) => n + (hiddenSubcategorySet.has(f.properties.subcategoryExternalId) ? 1 : 0);
    $[17] = hiddenSubcategorySet;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const hiddenNearbyCount = allFeatures.reduce(t10, 0);
  let t11;
  if ($[19] !== allFeatures || $[20] !== hiddenSubcategorySet || $[21] !== showHiddenPins) {
    t11 = showHiddenPins ? allFeatures : allFeatures.filter(f_0 => !hiddenSubcategorySet.has(f_0.properties.subcategoryExternalId));
    $[19] = allFeatures;
    $[20] = hiddenSubcategorySet;
    $[21] = showHiddenPins;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const visibleFeatures = t11;
  let t12;
  bb1: {
    if (!mapData) {
      t12 = null;
      break bb1;
    }
    let t13;
    if ($[23] !== mapData) {
      t13 = buildSubcategoryMap(mapData);
      $[23] = mapData;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== t13) {
      t14 = {
        subcategoryMap: t13
      };
      $[25] = t13;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    t12 = t14;
  }
  const lookups = t12;
  let t13;
  bb2: {
    const t14 = anchorPin ?? undefined;
    let t15;
    if ($[27] !== t14) {
      t15 = getFeatureImageUrl(t14);
      $[27] = t14;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    const anchorImage = t15;
    if (anchorImage) {
      t13 = anchorImage;
      break bb2;
    }
    let t16;
    if ($[29] !== nearbyPins) {
      const fallback = nearbyPins.find(_temp2);
      t16 = getFeatureImageUrl(fallback);
      $[29] = nearbyPins;
      $[30] = t16;
    } else {
      t16 = $[30];
    }
    const fallbackImage = t16;
    if (fallbackImage) {
      t13 = fallbackImage;
      break bb2;
    }
    let t17;
    if ($[31] !== steamId) {
      t17 = steamId ? getSteamImageUrl(steamId, "hero") : "";
      $[31] = steamId;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    t13 = t17;
  }
  const backgroundImageUrl = t13;
  let anchorTitle;
  let sidebarHeading;
  let t14;
  if ($[33] !== anchorPin || $[34] !== lookups?.subcategoryMap || $[35] !== t) {
    const anchorSubcategory = anchorPin ? lookups?.subcategoryMap.get(anchorPin.properties.subcategoryExternalId) ?? null : null;
    anchorTitle = anchorPin?.properties.title || anchorSubcategory?.title || "";
    sidebarHeading = anchorTitle ? t(_temp3, {
      pinName: anchorTitle
    }) : t(_temp4);
    t14 = anchorTitle ? t(_temp5, {
      pinName: anchorTitle
    }) : "";
    $[33] = anchorPin;
    $[34] = lookups?.subcategoryMap;
    $[35] = t;
    $[36] = anchorTitle;
    $[37] = sidebarHeading;
    $[38] = t14;
  } else {
    anchorTitle = $[36];
    sidebarHeading = $[37];
    t14 = $[38];
  }
  const saveDefaultTitle = t14;
  let t15;
  if ($[39] !== visibleFeatures) {
    t15 = visibleFeatures.map(_temp6);
    $[39] = visibleFeatures;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  const savePinIds = t15;
  const canShowSave = !!externalMapId && visibleFeatures.length > 0;
  let t16;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      setLoginModalTrigger("save-explore-list");
      setIsLoginModalOpen(true);
    };
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const openSaveLoginModal = t16;
  let t17;
  if ($[42] !== isLoggedIn) {
    t17 = () => {
      if (!isLoggedIn) {
        openSaveLoginModal();
        return;
      }
      setIsSaveModalOpen(true);
    };
    $[42] = isLoggedIn;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const handleSaveClick = t17;
  let t18;
  if ($[44] !== canShowSave || $[45] !== handleSaveClick || $[46] !== t) {
    t18 = canShowSave ? <Button onClick={handleSaveClick} color="primary" size="md">{t(_temp7)}</Button> : null;
    $[44] = canShowSave;
    $[45] = handleSaveClick;
    $[46] = t;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const headerAction = t18;
  let t19;
  if ($[48] !== anchorPinId || $[49] !== selectMarkerInMap) {
    t19 = () => anchorPinId && selectMarkerInMap(anchorPinId, {
      flyTo: true,
      selectSource: "sidebar_explore"
    });
    $[48] = anchorPinId;
    $[49] = selectMarkerInMap;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  let t20;
  if ($[51] !== anchorTitle || $[52] !== t) {
    t20 = anchorTitle || t(_temp8);
    $[51] = anchorTitle;
    $[52] = t;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  const t21 = !anchorPinId;
  let t22;
  if ($[54] !== anchorTitle || $[55] !== t19 || $[56] !== t20 || $[57] !== t21) {
    t22 = <ExploreActionHeaderIcon icon="sensors" tone="nearby" onClick={t19} title={anchorTitle} aria-label={t20} disabled={t21} />;
    $[54] = anchorTitle;
    $[55] = t19;
    $[56] = t20;
    $[57] = t21;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = css({
      textAlign: "center",
      wordBreak: "break-word"
    });
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== sidebarHeading) {
    t24 = <Type variant="header-m" color="white" className={t23} title={sidebarHeading}>{sidebarHeading}</Type>;
    $[60] = sidebarHeading;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t22 || $[63] !== t24) {
    t25 = <FlexColumn align="center" gap="12" width="full" data-drawer-drag="true">{t22}{t24}</FlexColumn>;
    $[62] = t22;
    $[63] = t24;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  const hero = t25;
  let t26;
  if ($[65] !== analytics || $[66] !== anchorPinId || $[67] !== externalMapId || $[68] !== hiddenCardDismissed || $[69] !== hiddenNearbyCount || $[70] !== hostType || $[71] !== showHiddenPins) {
    t26 = hiddenNearbyCount > 0 && !showHiddenPins && !hiddenCardDismissed ? <HiddenPinsCard hiddenCount={hiddenNearbyCount} onShow={() => {
      setShowHiddenPins(true);
      if (analytics && hostType) {
        analytics.event("game_map_explore_hidden_pins_click", {
          mapId: externalMapId ?? undefined,
          hiddenCount: hiddenNearbyCount,
          anchorLocationId: anchorPinId ?? undefined,
          platform: hostType
        });
      }
    }} onDismiss={() => setHiddenCardDismissed(true)} /> : undefined;
    $[65] = analytics;
    $[66] = anchorPinId;
    $[67] = externalMapId;
    $[68] = hiddenCardDismissed;
    $[69] = hiddenNearbyCount;
    $[70] = hostType;
    $[71] = showHiddenPins;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  const beforeItems = t26;
  const t27 = anchorPinId ?? undefined;
  const t28 = anchorPinId ?? null;
  let t29;
  if ($[73] !== backgroundImageUrl || $[74] !== beforeItems || $[75] !== headerAction || $[76] !== hero || $[77] !== isLoginModalOpen || $[78] !== isMobilePane || $[79] !== loginModalTrigger || $[80] !== onBack || $[81] !== onSearchFocus || $[82] !== t27 || $[83] !== t28 || $[84] !== visibleFeatures) {
    t29 = <PinListSidebarShell backgroundImageUrl={backgroundImageUrl} isMobilePane={isMobilePane} onBack={onBack} onSearchFocus={onSearchFocus} headerAction={headerAction} pinFeatures={visibleFeatures} hero={hero} beforeItems={beforeItems} feedbackSource="explore-list" feedbackAnchorLocationId={t27} selectSource="sidebar_explore" boundsFitKey={t28} isLoginModalOpen={isLoginModalOpen} onLoginModalOpenChange={setIsLoginModalOpen} loginModalTrigger={loginModalTrigger} onLoginModalTriggerChange={setLoginModalTrigger} />;
    $[73] = backgroundImageUrl;
    $[74] = beforeItems;
    $[75] = headerAction;
    $[76] = hero;
    $[77] = isLoginModalOpen;
    $[78] = isMobilePane;
    $[79] = loginModalTrigger;
    $[80] = onBack;
    $[81] = onSearchFocus;
    $[82] = t27;
    $[83] = t28;
    $[84] = visibleFeatures;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== dispatch || $[87] !== externalMapId || $[88] !== isSaveModalOpen || $[89] !== saveDefaultTitle || $[90] !== savePinIds) {
    t30 = externalMapId && <SaveChecklistModal isOpen={isSaveModalOpen} onClose={() => setIsSaveModalOpen(false)} defaultTitle={saveDefaultTitle} pinIds={savePinIds} mapExternalId={externalMapId} source="explore" onSaved={saved => {
      dispatch(setActiveCustomChecklistId(saved.id));
      dispatch(setSidebarView("customChecklist"));
    }} />;
    $[86] = dispatch;
    $[87] = externalMapId;
    $[88] = isSaveModalOpen;
    $[89] = saveDefaultTitle;
    $[90] = savePinIds;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  let t31;
  if ($[92] !== t29 || $[93] !== t30) {
    t31 = <>{t29}{t30}</>;
    $[92] = t29;
    $[93] = t30;
    $[94] = t31;
  } else {
    t31 = $[94];
  }
  return t31;
};
function _temp(state) {
  return state.auth.isAuthenticated;
}
function _temp2(pin) {
  return getFeatureImageUrl(pin);
}
function _temp3($) {
  return $.pins_near_$pinName;
}
function _temp4($_0) {
  return $_0.explore;
}
function _temp5($_1) {
  return $_1.save_checklist.near_$pinName;
}
function _temp6(f_1) {
  return f_1.properties.externalId;
}
function _temp7($_2) {
  return $_2.save_checklist.save;
}
function _temp8($_3) {
  return $_3.explore;
}